import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const DashboardMetrics = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState({
    pendingQuotes: 0,
    responseRate: 0,
    activeAreas: 0,
    activeCertifications: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadMetrics();
    }
  }, [user]);

  const loadMetrics = async () => {
    try {
      setLoading(true);

      // Get pending quotes count
      const { count: pendingCount } = await supabase?.from('quote_requests')?.select('*', { count: 'exact', head: true })?.eq('provider_id', user?.id)?.eq('status', 'pending');

      // Get total quotes for response rate
      const { count: totalQuotes } = await supabase?.from('quote_requests')?.select('*', { count: 'exact', head: true })?.eq('provider_id', user?.id);

      const { count: respondedQuotes } = await supabase?.from('quote_requests')?.select('*', { count: 'exact', head: true })?.eq('provider_id', user?.id)?.in('status', ['responded', 'accepted', 'completed']);

      // Get active service areas count
      const { count: areasCount } = await supabase?.from('provider_service_areas')?.select('*', { count: 'exact', head: true })?.eq('provider_id', user?.id);

      // Get active certifications count
      const { count: certsCount } = await supabase?.from('provider_certifications')?.select('*', { count: 'exact', head: true })?.eq('provider_id', user?.id)?.eq('status', 'active');

      const responseRate = totalQuotes > 0 ? Math.round((respondedQuotes / totalQuotes) * 100) : 0;

      setMetrics({
        pendingQuotes: pendingCount || 0,
        responseRate,
        activeAreas: areasCount || 0,
        activeCertifications: certsCount || 0,
      });
    } catch (error) {
      console.error('Error loading metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const metricCards = [
    {
      label: 'Openstaande Offertes',
      value: metrics?.pendingQuotes,
      icon: 'FileText',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
    },
    {
      label: 'Reactie Percentage',
      value: `${metrics?.responseRate}%`,
      icon: 'TrendingUp',
      color: 'text-success',
      bgColor: 'bg-success/10',
    },
    {
      label: 'Actieve Werkgebieden',
      value: metrics?.activeAreas,
      icon: 'MapPin',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      label: 'Actieve Certificaten',
      value: metrics?.activeCertifications,
      icon: 'Award',
      color: 'text-info',
      bgColor: 'bg-info/10',
    },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4]?.map((i) => (
          <div key={i} className="bg-card border border-border rounded-lg p-6 animate-pulse">
            <div className="h-4 bg-muted rounded w-1/2 mb-4"></div>
            <div className="h-8 bg-muted rounded w-1/3"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {metricCards?.map((metric, index) => (
        <div key={index} className="bg-card border border-border rounded-lg p-6 shadow-[0_8px_24px_-4px_rgba(0,0,0,0.12)] hover:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.18)] hover:-translate-y-2 transition-all duration-300 transform perspective-1000" style={{ transformStyle: 'preserve-3d' }}>
          <div className="flex items-center justify-between mb-4">
            <div className={`${metric?.bgColor} p-3 rounded-lg shadow-[0_4px_14px_0_rgba(0,0,0,0.1)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.15)] hover:-translate-y-1 transition-all duration-300 transform`}>
              <Icon name={metric?.icon} size={24} className={metric?.color} />
            </div>
          </div>
          <h3 className="text-2xl font-heading font-bold text-foreground mb-1">{metric?.value}</h3>
          <p className="text-sm text-muted-foreground">{metric?.label}</p>
        </div>
      ))}
    </div>
  );
};

export default DashboardMetrics;